<template>
  <div>
    <a-alert v-if="accountInformation && Object.values(accountInformation).length === 0" type="error" show-icon>
        <template #message>
          <div class="flex items-center justify-between">
            <div class="self-center">
              For generating bill, you should complete your company information in below path:
            </div>
            <a-button type="primary" ghost @click="$router.push('/settings?setting=account')">Complete Account</a-button>
          </div>
        </template>
    </a-alert>
    <vx-card class="mt-5" title="Select billable items to generate new bill.">
      <a-alert 
        message="In order to generate invoice, please select your Time and Expense entries."
        type="warning"
        show-icon
        class="mb-4"
      />
      <a-button 
        type="primary"
        :disabled="selectedRows.length === 0 || Object.values(accountInformation).length === 0"
        @click="openGenerate"
        class="mt-8"
        style="border-radius: 3px !important;"
      >
        Generate
      </a-button>
      <p v-if="selectedRows.length === 0" class="text-md mt-4" style="line-height: 22px;">
        No matter, time entry, or expense entry is selected.
      </p>
      <div v-else class="mt-4 flex flex-wrap gap-y-2">
        <a-tag v-for="(matter, index) in selectedRows" :key="matter.key" closable @close="removeMatter(matter)">
          {{ matter.title }}
        </a-tag>
      </div>
      <a-divider/>
      <!-- table  -->
      <ASpin id="tableGenerate" :spinning="loading" :indicator="indicator">
        <a-table
          class="overflow-auto"
          :rowClassName="(record)=> record.parent_id != null ? 'withOutBorder space-row': 'withBorder'"
          :columns="columns"
          :data-source="rows"
          :pagination="false"
        >
          <template slot="checkbox" slot-scope="text, record">
            <!-- :row-selection="{ selectedRowKeys: selectedRowKeys, onSelect: onSelectChange, getCheckboxProps, onSelectAll: onSelectAll, columnTitle: emptyVnode }" -->
            <a-checkbox 
              v-if="record.parent_id"
              :checked="selectedRowKeys.find(el => el.key === record.key).checked"
              :indeterminate="selectedRowKeys.find(el => el.key === record.key).indeterminate"
              @change="onSelectChange(record, $event.target.checked)"
            />
          </template>
          <span slot="actions" slot-scope="text,record">
            <span class="flex flex-col">
              <div v-if="record.parent_id === null" class="font-semibold">
                <template v-if="record.parent !== null && record.person">
                    {{ record.person.first_name }} {{ record.person.last_name }}
                </template>
                <template v-else>
                    {{ record.company.name }}
                </template>
              </div>
              <div v-else class="ml-5">
                 {{ record.title }}
                 <div class="flex mt-2">
                    <!-- Time Entries -->
                    <a-popover trigger="click" placement="bottom">
                      <div slot="content" style="text-align: center; min-width: 20rem;">
                        <a-spin :spinning="entryLoading" :indicator="indicator">
                          <template v-if="record.time_entries.length && !entryLoading">
                            <a-checkbox 
                              class="flex items-center"
                              style="margin-left: .6rem;"
                              :indeterminate="record.specific_times_check.time.indeterminate"
                              :checked="record.specific_times_check.time.checkAll"
                              @change="onTimeCheckAllChanged($event, record, 'time')"
                            >
                              <span class="pl-1">All</span>
                            </a-checkbox>
                            <a-checkbox 
                              v-for="time in record.time_entries"
                              :value="time"
                              :checked="record.specificTimes.includes(time.id)"
                              @change="handleChangeSpecific($event, time, record)"
                              class="flex items-center space-x-1"
                              :default-checked="record.specificTimes.includes(time.id)"
                            >
                              <div class="flex items-center space-x-2">
                                <div>{{ time.date }}</div>
                                <a-divider type="vertical"/>
                                <div>{{ `Duration: ${time.duration}` }}</div>
                                <a-divider type="vertical"/>
                                <div>{{ `Rate: ${time.rate}` }}</div>
                              </div>
                            </a-checkbox>
                          </template>
                          <template v-else-if="!entryLoading">This matter has no uninvoiced time entries.</template>
                        </a-spin>
                      </div>
                      <template slot="title">
                        <span>Time Entry</span>
                      </template>
                      <a-button type="link" @click="showTimeExpenseEntries(record.id)">Specific Time Entry</a-button>
                    </a-popover>

                    <!-- Expense Entries -->
                    <a-popover trigger="click" placement="bottom">
                      <div slot="content" style="text-align: center; min-width: 20rem;">
                        <a-spin :spinning="entryLoading" :indicator="indicator">
                          <template v-if="record.expense_entries.length && !entryLoading">
                            <a-checkbox 
                              class="flex items-center"
                              style="margin-left: .6rem;"
                              :indeterminate="record.specific_times_check.expense.indeterminate"
                              :checked="record.specific_times_check.expense.checkAll"
                              @change="onTimeCheckAllChanged($event, record, 'expense')"
                            >
                              <span class="pl-1">All</span>
                            </a-checkbox>
                            <a-checkbox 
                              v-for="expense in record.expense_entries"
                              :value="expense"
                              :checked="record.specificExpense.includes(expense.id)"
                              @change="handleChangeSpecific($event, expense, record)"
                              class="flex items-center space-x-1"
                            >
                              <div class="flex items-center space-x-2">
                                <div>{{ expense.date }}</div>
                                <a-divider type="vertical"/>
                                <div>{{ `Rate: ${expense.rate}` }}</div>
                                <a-divider type="vertical"/>
                                <div>{{ `Amount: ${expense.amount}` }}</div>
                              </div>
                            </a-checkbox>
                          </template>
                          <template v-else-if="!entryLoading">This matter has no uninvoiced expense entries.</template>
                        </a-spin>
                      </div>
                      <template slot="title">
                        <span>Expense Entry</span>
                      </template>
                      <a-button type="link" @click="showTimeExpenseEntries(record.id)">Specific Expense Entry</a-button>
                    </a-popover>
                 </div>
              </div>
            </span>
          </span>
          <span slot="hours" slot-scope="text,record">
            <span class="flex flex-col">
                 <div v-if="record.parent_id==null" class="font-semibold">
                    {{ Number.isInteger(record.contact_duration) ? record.contact_duration : record.contact_duration.toFixed(4) }}
                 </div>
                 <div v-else>
                   {{ Number.isInteger(record.matter_duration) ? record.matter_duration : record.matter_duration.toFixed(4) }}
                 </div>
            </span>
          </span>
          <span slot="amount" slot-scope="text,record">
            <span class="flex flex-col">
                 <div class="font-semibold" v-if="record.parent_id==null">
                   {{ record.contact_payable.toFixed(2) }}
                 </div>
                 <div v-else>
                   {{ record.matter_payable.toFixed(2) }}
                 </div>
            </span>
          </span>
          <span slot="expense" slot-scope="text,record">
            <span v-if="record.parent_id">{{ record.unbilled_expense.toFixed(2) }}</span>
            <strong v-else>{{ record.unbilled_expense.toFixed(2) }}</strong>
          
            <!-- <template v-if="record.parent_id">
              {{ calculateExpense(record.expense_entries) }}
            </template>
            <template v-else>
              <strong>{{ calculateTotalExpense(rows,record) }}</strong>
            </template> -->
          </span>

          <template slot="footer">
            <div class="flex justify-between items-center">
              <div class="flex items-center gap-x-4">
                <a-pagination 
                  v-model="pagination.page"
                  :total="pagination.total"
                  show-size-changer
                  :page-size.sync="pagination.limit"
                  :page-size-options="['5', '10', '20', '50']"
                  @change="getGenerateInvoice"
                  @showSizeChange="getGenerateInvoice"
                />
              </div>
              <!-- <div class="flex justify-around gap-x-16" style="font-size: 18px; font-weight: 500"> -->
                <!-- <div>Total amount:</div> -->
    <!--            <div>{{ Number.isInteger(total_payable) ? total_payable : total_payable.toFixed(2) }}</div>-->
                <!-- <div>{{ total_payable.toFixed(2) }}</div> -->
              <!-- </div> -->
            </div>
          </template>
        </a-table>
      </ASpin>

      <a-modal class="billing" title="Generate Invoices" :visible="open_generate" @cancel="open_generate = false" :width="750">
        <a-form :form="form" @submit.prevent="handleGenerateInvoices">

          <!--<SelectOption url="/account/list/" label="From user" field="from_user" :required="true" method="get" placeholder="Select an user" class="w-full"/>-->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <a-form-item label="Issue date" has-feedback>
              <a-date-picker
                v-decorator="['issue_date',{ initialValue: new Date().toISOString().split('T')[0],rules: [{ required: true, message: 'This filed is required.' }] }]"
                placeholder="dd/mm/yyyy"
                :format="dateFormat"
                valueFormat="YYYY-MM-DD"
                class="w-full"
              />
            </a-form-item>
            <a-form-item label="Due date" has-feedback>
              <a-date-picker
                v-decorator="['due_date',{ initialValue:new Date().toISOString().split('T')[0] ,rules: [{ required: true, message: 'This filed is required.' }] }]"
                placeholder="dd/mm/yyyy"
                :format="dateFormat"
                valueFormat="YYYY-MM-DD"
                class="w-full"
              />
            </a-form-item>
          </div>

          <a-form-item label="Detail Level" has-feedback>
            <a-select
              placeholder="condition"
              style="width: 100%"
              allowClear
              class="select"
              v-decorator="['detail_level',{ initialValue: '1' }]"
            >
              <a-select-option value="1">
                All Details
              </a-select-option>
              <a-select-option value="2">
                Activity Summary
              </a-select-option>
            </a-select>
          </a-form-item>

<!--          <a-form-item label="Discount">-->
<!--            <a-input-->
<!--              v-decorator="['discount',{ rules: [{ required: false, message: 'This filed is required.' }] }]"-->
<!--              @keypress="onlyNumber"-->
<!--              style="width: 150px"-->
<!--            >-->
<!--              <a-select-->
<!--                v-decorator="['discount_type',{ initialValue: '1',rules: [{ required: false, message: 'This filed is required.' }] }]"-->
<!--                slot="addonAfter"-->
<!--                @change="handleDiscount"-->
<!--                style="width: 60px"-->
<!--              >-->
<!--                <a-select-option value="1">-->
<!--                  %-->
<!--                </a-select-option>-->
<!--                <a-select-option value="2">-->
<!--                  RM-->
<!--                </a-select-option>-->
<!--              </a-select>-->
<!--            </a-input>-->
<!--          </a-form-item>-->


          <BillFaxComponent :form="form" />

          <div class="flex space-x-2">
            <a-button type="primary"  style="border-radius: 3px" html-type="submit">Save</a-button>
            <a-button type="primary" ghost style="border-radius: 3px" @click="open_generate = false">Cancel</a-button>
          </div>

        </a-form>
      </a-modal>
    </vx-card>
  </div>
</template>

<script>

import mixin from '../mixin/mixin'
import SelectOption from "../forms/SelectOptionSingle"
import selectSearch from "@/views/forms/selectSearch";
import axios from  '@/axios'
import BillFaxComponent from "@/components/bill-fax/BillFaxComponent";

const columns = [
  {
    title: '',
    dataIndex: 'Checkbox',
    scopedSlots: { customRender: 'checkbox' },
  },
  {
    title: 'Client',
    dataIndex: 'Billable',
    scopedSlots: { customRender: 'actions' },
  },
  {
    title: 'Unbilled Hours',
    dataIndex: 'entries',
    scopedSlots: { customRender: 'hours' },
  },
  {
    title: 'Unbilled Expense',
    dataIndex: 'expense',
    scopedSlots: { customRender: 'expense' },
  },
  {
    title: 'Amount Due',
    dataIndex: 'amount',
    scopedSlots: { customRender: 'amount' },
  }
]

export default {
  name: 'BillingAddBetaView',

  components:{
    BillFaxComponent,
    SelectOption,
    selectSearch
  },

  mixins: [mixin],
  
  data() {
    return {
      selectedRowKeys : [],
      open_generate   : false,
      discount_type   : '1',
      generates       : [],
      loading         : false,
      entryLoading    : false,
      columns,
      rows            : [],
      selectedRows    : [],
      total_payable   : 0,
      pagination      : { page: 1, total: undefined, limit: 10 },
      accountInformation: null,
      hasAllSelectCheck: false,
      indicator       : <a-icon type="loading" style="font-size: 24px" spin />,
      emptyVnode      : <span />,
    }
  },

  methods: {
    openGenerate() {
      this.open_generate = true
    },

    calculateExpense(expense) {
      return expense.map(({amount})=>amount).reduce((item,x)=> item + x,0).toFixed(2)
    },
    calculateTotalExpense(rows,record){
      const filter = rows.filter(({parent_id,client_id}) => parent_id && client_id === record.id).map(({expense_entries}) => expense_entries)
      let sum = 0;
      for(let i = 0; i < filter.length;i++){
        for(let j = 0; j < filter[i].length;j++)
          sum += filter[i][j].amount
      }
      return sum.toFixed(2)
    },

    onSelectAll(selected, selectedRows) {
      /* selected ? this.hasAllSelectCheck = true : this.hasAllSelectCheck = false
      const selectedAll    = selectedRows.map(row=>row.key)
      const selectedFilter = selectedRows.filter(row=>row.parent_id !== null)
      selected ? this.selectedRowKeys = selectedAll : this.selectedRowKeys = []
      let map              = new Map(selectedFilter.map(item => [item.id,item]));
      this.selectedRows    = [...map.values()]; */
    },

    async onSelectChange(record, selected) {
      const isContact = record.parent_id === null // Either is a matter or a contact.

      if (selected) {
        // this.selectedRowKeys.push({ key: record.key, checked: true, indeterminate: false })
        this.selectedRowKeys.find(el => el.key === record.key).checked = true
        this.selectedRowKeys.find(el => el.key === record.key).indeterminate = false
        
        /* if (isContact) {
          const children = this.rows.filter(item => item.parent_id === record.id)
          children.forEach(child => {
            if (!this.selectedRowKeys.includes(child.key)) {
              this.selectedRowKeys.push(child.key);
              this.selectedRows.push(child)
              let map           = new Map(this.selectedRows.map(item => [item.id,item]));
              this.selectedRows = [...map.values()];
            }
          })
        } */
        // else {
        if (!record.specific_times_check.loaded) {
          this.loading = true
          await this.loadTimeExpenseEntries(record.id)
          this.loading = false
        }

        const child = this.rows.find(item => item.key === record.key) || {}
        this.selectedRows.push(child)
        let map = new Map(this.selectedRows.map(item => [item.id, item]))
        this.selectedRows = [...map.values()]
        // }
      }
      else {
        // let indexParent = this.selectedRowKeys.findIndex(i => i === record.key)
        // if (indexParent > -1) this.selectedRowKeys.splice(indexParent, 1)
        this.selectedRowKeys.find(el => el.key === record.key).checked = false
        this.selectedRowKeys.find(el => el.key === record.key).indeterminate = false
        
        /* if (isContact) {
          const child = this.rows.filter(item => item.parent_id === record.id)
          child.forEach(child => {
            const index = this.selectedRowKeys.findIndex((ic)=> ic === child.key)
            const indexChild = this.selectedRows.findIndex((item)=> item.key === child.key)
            if(index > -1){
              this.selectedRows.splice(indexChild,1)
              this.selectedRowKeys.splice(index,1)
            }
          })
        } */
        // else {
          let indexRow = this.selectedRows.findIndex((i)=>i.key === record.key);
          if (indexRow > -1) this.selectedRows.splice(indexRow,1)
        // }
      }

      this.onTimeCheckAllChanged({target: {checked: selected }}, record, 'time')
      this.onTimeCheckAllChanged({target: {checked: selected }}, record, 'expense')
    },

    /* getCheckboxProps(record) {
      const isMatter = record.parent_id !== null

      if (!isMatter) {
        return {
          style: (!record.parent_id) ? 'display: none' : 'display: block',
        }
      }
      else return {}
    }, */

    async getGenerateInvoice() {
      try {
        this.loading = true
        const { data } = await axios.get(`/billing/unpaid-items-new?page=${this.pagination.page}&page_size=${this.pagination.limit}`)
        this.pagination.total = data.total_items
        this.rows = []
        this.total_payable = 0

        data.items.forEach(el => {
          let clientId = this.rows.findIndex(row => row.id === el[0])

          if (clientId === -1) {
            clientId = this.rows.push({
              key             : `contact_${el[0]}`,
              id              : el[0],
              person          : (el[8].id) ? el[8] : null,
              company         : (el[7].id) ? el[7] : null,
              contact_duration: 0,
              contact_payable : 0,
              unbilled_expense: 0,
              parent_id       : null,
            }) - 1
          }

          this.rows.push({
            key                 : `matter_${el[1]}`,
            id                  : el[1],
            title               : el[2],
            time_entries        : [], // matter.time_entries,
            expense_entries     : [], // Number(el[6]),
            unbilled_expense    : Number(el[6]),
            parent_id           : el[0],
            matter_duration     : el[4],
            matter_payable      : Number(el[3]) + Number(el[6]),
            client_id           : el[0],
            specificTimes       : [],
            specificExpense     : [],
            specific_times_check: {
              loaded : false,
              time   : { indeterminate: false, checkAll: false },
              expense: { indeterminate: false, checkAll: false }
            }
          })

          this.rows[clientId].contact_duration += el[4]
          this.rows[clientId].contact_payable += Number(el[3]) + Number(el[6])
          this.rows[clientId].unbilled_expense += Number(el[6])

          this.total_payable += Number(el[3]) + Number(el[6])
        })

        this.rows.forEach(row => {
          this.selectedRowKeys.push({ key: row.key, checked: false, indeterminate: false })
        })
      }
      catch (e) {
        this.$message.error('Error receiving data')
      }
      finally {
        this.loading = false
      }
    },

    hasTimeEmpty(record) {
      if(record.specificTimes.length)
        return record.specificTimes
      if (
        record.specificTimes.length === record.time_entries.length ||
        (record.specificExpense.length === 0 && record.specificTimes.length === 0)
      )
        return record.time_entries.map(ele => ele.id)
      return []
    },

    hasExpenseEmpty(record) {
      if(record.specificExpense.length)
        return record.specificExpense
      if (
        record.specificExpense.length === record.expense_entries.length ||
        (record.specificExpense.length === 0 && record.specificTimes.length === 0)
      )
        return record.expense_entries.map(ele => ele.id)
      return []
    },

    handleGenerateInvoices() {
      this.form.validateFields((err, values) => {
        if (!err) {
          this.$vs.loading()
          this.selectedRows.map(async (item) => await axios.post('/billing/', {
            ...values,
            to_contact     : item.parent_id || item.person.contact_id,
            matters        : [item.id],
            time_entries   : this.hasTimeEmpty(item),
            expense_entries: this.hasExpenseEmpty(item)
          })
          .then(response => {
            this.open_generate = false
            this.$notification['success']({
              message: 'Success',
              description: `Generate invoices created.`,
            })
            this.$router.push(`/billing/dashboard/${response.data.id}`)
          })
          .catch(() => {
            this.open_generate = true
            this.$notification['error']({
              message: 'error',
              description: `Generate invoices failed.`,
              duration: 1.5
            })
          })
          .finally(() => {
            this.$vs.loading.close()
          }))
        }
      })
    },

    handleChangeSpecific(e, specificEntry, record) {
      const isExpense         = specificEntry.quantity !== undefined
      const entryType         = (isExpense) ? 'expense' : 'time'
      const entryPropertyName = (isExpense) ? 'specificExpense' : 'specificTimes'
      
      if (e.target.checked) {
        if (!record[entryPropertyName].includes(specificEntry.id))
          record[entryPropertyName].push(specificEntry.id)
        
        if (!this.selectedRows.includes(record))
          this.selectedRows.push(record)
      }
      else {
        const index = record[entryPropertyName].findIndex(id => id === specificEntry.id)
        if (index !== -1)
          record[entryPropertyName].splice(index, 1)
        
        if (record[entryPropertyName].length === 0) {
          const rowIndex = this.selectedRows.findIndex(item => item.key === record.key)
          if (rowIndex !== -1)
            this.selectedRows.splice(rowIndex, 1)
        }
      }

      record.specific_times_check[entryType].indeterminate =
        Boolean(record[entryPropertyName].length) &&
        record[entryPropertyName].length < record[`${entryType}_entries`].length
      
      record.specific_times_check[entryType].checkAll =
        record[entryPropertyName].length === record[`${entryType}_entries`].length


      let allTimeEntriesChecked = 'unavailable', allExpenseEntriesChecked = 'unavailable'
      if (record.time_entries.length) {
        if (record.specific_times_check.time.checkAll) allTimeEntriesChecked = 'all'
        else if (record.specificTimes.length === 0) allTimeEntriesChecked = 'none'
        else allTimeEntriesChecked = 'indeterminate'
      }
      if (record.expense_entries.length) {
        if (record.specific_times_check.expense.checkAll) allExpenseEntriesChecked = 'all'
        else if (record.specificExpense.length === 0) allExpenseEntriesChecked = 'none'
        else allExpenseEntriesChecked = 'indeterminate'
      }

      if ((allTimeEntriesChecked === 'all' || allTimeEntriesChecked === 'unavailable') && (allExpenseEntriesChecked === 'all' || allExpenseEntriesChecked === 'unavailable')) {
        this.selectedRowKeys.find(el => el.key === record.key).checked = true
        this.selectedRowKeys.find(el => el.key === record.key).indeterminate = false
      }
      else if ((allTimeEntriesChecked === 'none' || allTimeEntriesChecked === 'unavailable') && (allExpenseEntriesChecked === 'none' || allExpenseEntriesChecked === 'unavailable')) {
        this.selectedRowKeys.find(el => el.key === record.key).checked = false
        this.selectedRowKeys.find(el => el.key === record.key).indeterminate = false
      }
      else {
        this.selectedRowKeys.find(el => el.key === record.key).checked = false
        this.selectedRowKeys.find(el => el.key === record.key).indeterminate = true
      }
    },

    calculateDiscount(item) {
      const { discount_type }  = this
      const filterTimes        = item.time_entries.filter(item => item.show !== undefined && item.show).map((item) => item.rate * item.duration)
      const filterExpense      = item.expense_entries.filter(item => item.show !== undefined && item.show).map((item)=> item.rate * item.quantity)
      const timesTotal         = (item.time_entries && item.time_entries.length) ? filterTimes.reduce((item,x)=> item + x,0) : 0
      const expensesTotal      = (item.expense_entries && item.expense_entries.length) ? filterExpense.reduce((item,x)=> item + x,0) : 0
      const hasDetailSpecific  = (filterTimes.length || filterExpense.length)
      const total              = hasDetailSpecific ? expensesTotal + timesTotal : item.matter_payable
      if (this.form.getFieldValue('discount') && discount_type) {
        if (discount_type === '1') return (total * this.form.getFieldValue('discount')) / 100
        if (discount_type === '2') return total - this.form.getFieldValue('discount')
      }
      return undefined
    },

    async getAccountInformation() {
      try {
        const { data } = await axios.get('/setting/firm/')
        this.accountInformation = data
      }
      catch (e) {
        this.$message.error('Error receiving data')
      }
    },

    async loadTimeExpenseEntries(matterId) {
      const matterIndex = this.rows.findIndex(el => el.parent_id !== null && el.id === matterId)
      
      if (matterIndex !== -1 && !this.rows[matterIndex].specific_times_check.loaded) {
        try {
          const { data } = await axios.post('billing/unpaid-items-entries/', { matters: [matterId] })
          if (data.rows.length && data.rows[0].matters.length) {
            if (!this.rows[matterIndex].expense_entries.length)
              this.rows[matterIndex].expense_entries = data.rows[0].matters[0].expense_entries
            if (!this.rows[matterIndex].time_entries.length)
              this.rows[matterIndex].time_entries = data.rows[0].matters[0].time_entries
          }
          this.rows[matterIndex].specific_times_check.loaded = true
        }
        catch (e) {
          this.$message.error('Error receiving data')
        }
      }
    },

    async showTimeExpenseEntries(matterId) {
      this.entryLoading = true
      await this.loadTimeExpenseEntries(matterId)
      this.entryLoading = false
    },

    /**
     * @param {String} mode
     * Its value must be either "time" or "expense".
     */
    onTimeCheckAllChanged(e, record, mode) {
      if (mode !== 'time' && mode !== 'expense')
        throw new Error(`Invalid argument: Parameter mode must be either "time" or "expense", but ${mode} was passed.`)

      record.specific_times_check[mode].indeterminate = false
      record.specific_times_check[mode].checkAll = e.target.checked

      record[`${mode}_entries`].forEach(time => {
        this.handleChangeSpecific(e, time, record)
      })
    },

    removeMatter(matter) {
      matter.time_entries.forEach(time => {
        this.handleChangeSpecific({target: {checked: false}}, time, matter)
      })
      matter.expense_entries.forEach(time => {
        this.handleChangeSpecific({target: {checked: false}}, time, matter)
      })
      this.onSelectChange(matter, false)
    },
  },

  mounted() {
    this.getAccountInformation()
    this.getGenerateInvoice()
  }
}
</script>

<style>
  @import "../style/style.css";
  .ant-btn-background-ghost.ant-btn-link,.ant-btn-link{
    color: #3A3DBE !important;
  }
  .table tr td{
    position: relative;
    left: 1em;
  }
  .withBorder td{
    border-top: 1px solid #e8e8e8 !important;
    border-bottom: none !important;
  }
  .withOutBorder td{
    border: none !important
  }
  .space-row .ant-table-selection-column{
    padding-left: 4em;
  }
</style>
